<script setup lang="ts">
import { useDebounceFn, useIntervalFn } from '@vueuse/core'

import axios from 'axios'
import AudienceRulesPage from './AudienceRulesPage.vue'
import AudienceTitle from './components/AudienceTitle.vue'

import {
  ArrowLeft,
  ChevronDown,
  Clock,
  Pencil,
  Plus,
  RefreshCw,
  Store,
  TextCursorInput,
  Trash2,
  Users
} from 'lucide-vue-next'
import CustomerModal from '../customer/Components/CustomerModal.vue'
import AudienceStoreChips from './components/AudienceStoreChips.vue'
import AudienceStoreSelectField from './components/AudienceStoreSelectField.vue'
import type { Audience, AudienceRule } from '@/types/types'
import type { TabKey } from '@/components/base/BaseTab.vue'
import type { MarketingFlow } from '@/views/autopilot/MarketingFlowPage.vue'
import { isMarketingFlowBlockingAudienceEdit } from '@/views/autopilot/utils'
import { uniqBy } from 'lodash'
import { collapseFullStoreScope, expandGlobalStoreScope } from '@/utils/audienceStoreIds'
import { useContentScopeSave } from '@/composables/contentScope'
import type { ContentScope } from '@/types/contentScope'

const {
  fetchMerchantAudienceMass,
  fetchMerchantAudienceCustomersCount,
  fetchAudienceById,
  editAudienceName,
  editAudienceScope,
  editAudienceStores,
  formatAudienceRulesRecordToArray,
  handleSubmitSegmentRules,
  associateSegmentToCustomer,
  deleteAudience
} = useAudience()
const { localDate } = useDate()
const { t } = useI18n()
const notification = useNotification()
const router = useRouter()
const route = useRoute()

const { customerErrors, gendersList, merchantAttributes, addCustomer, isCustomerLoading } = useCustomer()

const hasRulesError = ref(false)

const selectedCustomerId = ref(null)
const customers = ref([])
const audienceId = ref(null)
const audience = ref(null)
const audienceCustomersNewCount = ref(null)
const audienceCustomersTotal = ref(0)

const { value: contactCountValue, label: contactCountLabel } = useHeaderCount(
  () =>
    audienceCustomersTotal.value
      ? { filtered: audienceCustomersTotal.value, total: audienceCustomersTotal.value }
      : null,
  () => 'global.words.contact'
)
const areCustomersLoading = ref(false)
const isEditing = ref(route.query.fromTemplate ? true : false)
const isLoading = ref(true)
const isCreatingCustomer = ref(false)
const isSearchingCustomers = ref(false)
const audienceTitleRef = ref<InstanceType<typeof AudienceTitle> | null>(null)
const isHeaderLoading = ref(false)
const searchText = ref<string>('')
const searchList = ref([])
const searchTable = {
  columns: {
    customer: {
      label: t('pages.customer.audienceDetailPage.table.columns.customer')
    },
    add: {
      label: ''
    }
  },
  pagination: {
    total: null,
    currentPage: 1,
    pageSize: null,
    lastPage: null
  }
}

const defaultPagination = {
  total: 1,
  currentPage: 1,
  pageSize: null,
  lastPage: null
}

const initTablePagination = () => {
  if (searchTable.pagination) {
    const clonedDefaultPagination = JSON.parse(JSON.stringify(defaultPagination))
    Object.assign(searchTable.pagination, clonedDefaultPagination)
  }
}

const isCustomerInAudience = (customerId) => {
  if (!Array.isArray(customers.value)) {
    return false
  }

  return customers.value.some((customer) => customer.id === customerId)
}

const fetchCustomers = async (page = 1) => {
  const params = {
    search: searchText.value || '',
    page: page,
    // /customers does explode(',', $storeIds) server-side — needs a comma-joined string, not an array.
    ...(audience.value?.stores?.length && { storeIds: audience.value.stores.map((store) => store.id).join(',') })
  }

  try {
    const response = await axios.get('/customers', { params })
    const { data, meta } = response.data

    if (Array.isArray(data) && data.length > 0) {
      searchList.value = data.map((customer) => ({
        id: customer.id,
        data: {
          customer: {
            id: customer.id,
            phone: customer.phone,
            name: customer.firstName,
            email: customer.email,
            loyaltyCardNumber: customer.loyaltyCardNumber,
            gender: customer.gender
          },
          add: !isCustomerInAudience(customer.id)
        }
      }))

      Object.assign(searchTable.pagination, {
        currentPage: meta.current_page || 1,
        pageSize: meta.per_page || 10,
        total: meta.total || 0,
        lastPage: meta.last_page || 1
      })
    } else {
      notification.create('error', {
        text: t('pages.customer.audienceDetailPage.search.empty')
      })
    }
  } catch {
    notification.create('error', {
      text: t('pages.customer.audienceDetailPage.search.error')
    })
  }
}

const searchCustomer = useDebounceFn(async (page) => await fetchCustomers(page), 500)

const newCustomer = ref({
  phone: null,
  points: 0,
  referenceStore: null,
  country: null,
  email: '',
  loyaltyCardNumber: null,
  optin: null,
  hasOptinEmail: null,
  hasCheckin: true,
  nationalNumber: null,
  firstname: '',
  lastname: '',
  gender: gendersList[0].id,
  birthday: null,
  birthdayFormatted: null,
  tags: [],
  postalCode: null,
  city: null,
  lists: []
})
const selectedAttributes = ref()

const emptyState = computed(() => {
  return {
    dynamic: {
      title: t('pages.customer.audienceDetailPage.emptyState.dynamic.title'),
      description: t('pages.customer.audienceDetailPage.emptyState.dynamic.description'),
      secondaryButton: {
        text: t('pages.customer.audienceDetailPage.emptyState.dynamic.secondaryBtn'),
        icon: 'HeroPencilIconSolid',
        isOutlined: true,
        callBackFn: () => editSegmentRules()
      }
    },
    static: {
      title: t('pages.customer.audienceDetailPage.emptyState.static.title'),
      description: t('pages.customer.audienceDetailPage.emptyState.static.description'),
      secondaryButton: {
        text: t('pages.customer.audienceDetailPage.emptyState.secondary.btn'),
        icon: 'HeroPlusIconSolid',
        isOutlined: true,
        callBackFn: () => openModal('isAddCustomerModalOpen')
      },
      primaryButton: {
        text: t('pages.customer.audienceDetailPage.emptyState.primary.btn'),
        icon: 'HeroArrowDownTrayIconSolid',
        callBackFn: () =>
          router.push({
            name: 'Import',
            query: {
              audience: audienceId.value,
              ...(audience.value?.stores?.length && {
                storeIds: audience.value.stores.map((store) => store.id).join(',')
              })
            }
          })
      }
    }
  }
})

const isSegmentProcessing = ref(false)

const crumbs = ref([
  {
    name: t('pages.customer.audiences.header.crumbs'),
    route: 'AudienceList'
  },
  {
    name: '',
    description: ''
  }
])

const pageTitle = computed(() => {
  return crumbs.value[1]
})

const isTipVisible = computed(() => isSegmentProcessing.value)

const PROCESSING_POLL_INTERVAL_MS = 5000

// Nothing pushes the end of a recomputation to the page: poll until the API reports it done,
// or the banner (and the contacts skeleton) would stay until the user reloads.
const { pause: stopProcessingPoll, resume: startProcessingPoll } = useIntervalFn(
  async () => {
    // A failed check is retried on the next tick rather than surfaced.
    const audienceDetails = await fetchAudienceById(audienceId.value).catch(() => null)
    if (!audienceDetails || audienceDetails.is_processing) return

    isSegmentProcessing.value = false
    audience.value.is_processing = false
    await getCustomersCount()
    await getCustomers()
  },
  PROCESSING_POLL_INTERVAL_MS,
  { immediate: false }
)

watch(isSegmentProcessing, (isProcessing) => (isProcessing ? startProcessingPoll() : stopProcessingPoll()), {
  immediate: true
})

const isEmptyStateVisible = computed(() => {
  return (
    !customers.value.length &&
    !areCustomersLoading.value &&
    !isSegmentProcessing.value &&
    (audience.value?.type === 'static' || !(audienceCustomersNewCount.value > 0))
  )
})

const currentTab = ref((route?.query?.activeTab as string) || 'contacts')
const changeTab = (selectedTab: string) => {
  if (selectedTab !== 'rules') isEditing.value = false
}
const tabs = ref<TabKey[]>([
  {
    name: t('pages.customer.audienceDetailPage.tab.contacts'),
    value: 'contacts'
  }
])

const table = reactive({
  pagination: {
    pageSize: null,
    page: 1,
    currentPage: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false
  },
  columns: {
    customer: {
      label: t('pages.customer.audienceDetailPage.table.columns.customer')
    },
    points: {
      label: t('pages.customer.audienceDetailPage.table.columns.points'),
      numeric: true
    },
    checkinCount: {
      label: t('pages.customer.audienceDetailPage.table.columns.checkinCount'),
      numeric: true
    },
    registerDate: {
      label: t('pages.customer.audienceDetailPage.table.columns.registerDate')
    }
  }
})

const rows = ref([])

const getTableData = () => {
  if (Array.isArray(customers.value) && customers.value.length > 0) {
    rows.value = customers.value.map((customer) => {
      customer = {
        id: customer.id,
        data: {
          customer: {
            id: customer.id,
            phone: customer.phone,
            name: customer.firstName,
            email: customer.email,
            loyaltyCardNumber: customer.loyaltyCardNumber,
            gender: customer.gender,
            hasOptin: customer.hasOptin,
            countryFlag: customer.countryFlag
          },
          points: customer.points,
          checkinCount: customer.checkinCount,
          registerDate: localDate(customer.registerDate)
        }
      }
      if (audience.value?.type === 'static') {
        table.columns.options = {
          label: t('pages.customer.audienceDetailPage.table.columns.options')
        }
        customer.data.options = null
      }
      return customer
    })
  } else {
    rows.value = []
  }
}

const getAudience = async () => {
  try {
    isHeaderLoading.value = true

    const audienceDetails = await fetchAudienceById(audienceId.value)

    if (audienceDetails) {
      audience.value = audienceDetails

      if (crumbs.value && crumbs.value.length > 1) {
        crumbs.value[1].name = audience.value.name || ''
      }
      if (audience.value.type === 'dynamic') {
        if (!tabs.value.some((tab) => tab.value === 'rules')) {
          tabs.value.push({
            value: 'rules',
            name: t('pages.customer.audienceDetailPage.tab.rules')
          })
        }
      }
    }
  } catch (error) {
    notification.create('error', {
      text: t('pages.customer.audienceDetailPage.notifications.loading.error')
    })
    throw new Error(error)
  } finally {
    isHeaderLoading.value = false
  }
}

let cancelGetAudienceCustomers

const getCustomers = async (withProcess = false) => {
  if (cancelGetAudienceCustomers) {
    cancelGetAudienceCustomers.cancel('Operation canceled due to new request.')
  }
  cancelGetAudienceCustomers = axios.CancelToken.source()

  areCustomersLoading.value = true
  try {
    const response = await fetchMerchantAudienceMass(
      audienceId.value,
      table.pagination,
      table.sort,
      cancelGetAudienceCustomers.token
    )

    if (response && response.data) {
      customers.value = response.data

      if (withProcess) {
        isSegmentProcessing.value = response.meta.is_processing
      }

      const { per_page, current_page } = response.meta
      const { next, prev } = response.links || {}

      Object.assign(table.pagination, {
        pageSize: per_page,
        currentPage: current_page,
        total: audienceCustomersTotal.value,
        hasNextPage: !!next,
        hasPrevPage: !!prev
      })

      getTableData()

      if (addCustomerModalPropsIndex.value !== 2 && audience.value.type === 'static' && isAddCustomerModalOpen.value) {
        searchText.value = ''
        searchCustomer(null)
      }
    } else {
      throw new Error('Invalid response data')
    }
  } catch (error) {
    if (cancelGetAudienceCustomers) {
      return
    } else {
      notification.create('error', {
        text: t('pages.customer.audienceDetailPage.notifications.loading.customers.error')
      })
      throw new Error(`Error fetching customers: ${error}`)
    }
  } finally {
    areCustomersLoading.value = false
  }
}

const relatedMarketingFlows = ref<MarketingFlow[]>([])

const blockingMarketingFlows = computed(() => relatedMarketingFlows.value.filter(isMarketingFlowBlockingAudienceEdit))

const getAutopilots = async () => {
  const { data } = await axios.get(`/marketing-flows?segment_id=${audienceId.value}`)

  relatedMarketingFlows.value = data.data
}

const getCustomersCount = async () => {
  try {
    const total = await fetchMerchantAudienceCustomersCount(audienceId.value)
    audienceCustomersTotal.value = total
  } catch (error) {
    console.error('Error fetching customers count:', error)
    audienceCustomersTotal.value = 0
  }
}

const isRemoveAudienceModalOpen = ref(false)
const isRemoveCustomerModalOpen = ref(false)
const isAddCustomerModalOpen = ref(false)
const isEditStoresModalOpen = ref(false)
const isSavingStores = ref(false)
const editingStoreIds = ref<number[]>([])

const targetableAudienceStores = computed(() => (audience.value?.stores ?? []).filter((store) => !store.is_churned))

const merchantStoreIds = computed(() => (audience.value?.merchant_stores ?? []).map((store) => store.id))

const additionalStoreOptionsForEdit = computed(() =>
  uniqBy([...(audience.value?.merchant_stores ?? []), ...targetableAudienceStores.value], 'id').map((store) => ({
    value: store.id,
    label: store.name
  }))
)

const deleteModalProps = {
  customer: {
    title: t('pages.customer.audienceDetailPage.delete.title'),
    text: t('pages.customer.audienceDetailPage.delete.text'),
    linkText: t('pages.customer.audienceDetailPage.delete.linkText'),
    cancel: t('global.words.cancel'),
    iconColor: 'red',
    icon: 'HeroExclamationTriangleIconSolid'
  }
}

const deleteAudienceModalProps = {
  title: t('pages.customer.audienceDetailPage.deleteAudience.title'),
  text: t('pages.customer.audienceDetailPage.deleteAudience.text'),
  linkText: t('pages.customer.audienceDetailPage.delete.linkText'),
  cancel: t('global.words.cancel'),
  iconColor: 'red',
  icon: 'HeroExclamationTriangleIconSolid'
}

const modals: Record<string, Ref<boolean>> = {
  isRemoveAudienceModalOpen,
  isRemoveCustomerModalOpen,
  isAddCustomerModalOpen
}

const actions = computed(() => {
  return [
    {
      id: 'renameAudience',
      text: t('pages.customer.audienceDetailPage.actions.editAudienceName'),
      show: !audience.value.frequency_type,
      icon: TextCursorInput
    },
    {
      id: 'addManually',
      text: t('pages.customer.audienceDetailPage.actions.static.addManually'),
      show: audience.value.type === 'static',
      icon: Plus
    },
    {
      id: 'editSegmentRules',
      text: t('pages.audience.audienceDetailPage.actions.editSegmentRules'),
      show: audience.value.type === 'dynamic',
      icon: Pencil
    },
    {
      id: 'editAudienceStores',
      text: t('pages.customer.audienceDetailPage.actions.editAudienceStores'),
      show: audience.value.type === 'static',
      icon: Store
    },
    {
      id: 'deleteAudience',
      text: t(`pages.customer.audienceDetailPage.actions.${audience.value?.type}.deleteAudience`),
      show: areCustomersLoading.value === false && customers.value?.length === 0 && !audience.value.frequency_type,
      icon: Trash2,
      variant: 'destructive' as const
    }
  ].filter((action) => action.show)
})
const handleDropdownUpdate = (action) => {
  if (action === 'editSegmentRules') {
    editSegmentRules()
  } else if (action === 'addManually') {
    openModal('isAddCustomerModalOpen')
  } else if (action === 'deleteAudience') {
    openModal('isRemoveAudienceModalOpen')
  } else if (action === 'renameAudience') {
    startEditingName()
  } else if (action === 'editAudienceStores') {
    openEditStoresModal()
  }
}

const openEditStoresModal = () => {
  editingStoreIds.value = expandGlobalStoreScope(
    targetableAudienceStores.value.map((store) => store.id),
    merchantStoreIds.value
  )
  isEditStoresModalOpen.value = true
}

const isPongoGeneratedAudience = computed(() => Boolean(audience.value.frequency_type))

const saveScope = useContentScopeSave()

const handleUpdateScope = async (scope: ContentScope) => {
  await saveScope(async () => {
    await preserveProcessingFlag(() => editAudienceScope(audience.value, scope))
    return audience.value.scope ?? scope
  })
}

// update() doesn't return is_processing (unlike create()/show()) - preserve it.
const preserveProcessingFlag = async (updateFn: () => Promise<Audience>) => {
  const wasProcessing = audience.value.is_processing
  audience.value = await updateFn()
  audience.value.is_processing = wasProcessing
}

const handleSaveStores = async () => {
  isSavingStores.value = true
  try {
    await preserveProcessingFlag(() =>
      editAudienceStores(audience.value, collapseFullStoreScope(editingStoreIds.value, merchantStoreIds.value))
    )
    isEditStoresModalOpen.value = false
  } catch {
    // editAudienceStores already shows the error toast; keep the modal open for a retry.
  } finally {
    isSavingStores.value = false
  }
}

const startEditingName = () => {
  audienceTitleRef.value?.startEditingName()
}

const handleUpdateTitle = async (newTitle: string) => {
  isHeaderLoading.value = true
  audience.value.name = newTitle
  await preserveProcessingFlag(() => editAudienceName(audience.value))
  crumbs.value[1].name = audience.value.name
  isHeaderLoading.value = false
}

const editSegmentRules = () => {
  if (route.query.activeTab !== 'rules') {
    currentTab.value = 'rules'
  }

  // editedStoreIds only gets a real value once the user touches the store field
  // (AudienceRulesPage only emits update:storeIds on change) - seed it from the
  // audience's current scope here, or saving rules without touching stores would
  // read it as [], see hasStoreIdsChanged() as a scope change, and wipe it.
  editedStoreIds.value = [...(audience.value?.store_ids ?? [])]
  isEditing.value = true
}

const newRules = ref<Record<string, AudienceRule>>()
const editedStoreIds = ref<number[]>([])

const hasStoreIdsChanged = computed(() => {
  const current = [...(audience.value?.store_ids ?? [])].sort()
  const edited = [...editedStoreIds.value].sort()
  return current.length !== edited.length || current.some((id, index) => id !== edited[index])
})

const saveRules = async () => {
  currentTab.value = 'contacts'
  isSegmentProcessing.value = true
  getCustomers()
  // The API rejects a dynamic audience left with neither rule nor store, checked against the
  // other field's current value: save whichever side still defines the audience first.
  const saveStores = async () => {
    if (hasStoreIdsChanged.value) {
      await preserveProcessingFlag(() => editAudienceStores(audience.value, editedStoreIds.value))
    }
  }
  try {
    if (formatAudienceRulesRecordToArray(newRules.value).length > 0) {
      await handleSubmitSegmentRules(audienceId.value, newRules.value)
      await saveStores()
    } else {
      await saveStores()
      await handleSubmitSegmentRules(audienceId.value, newRules.value)
    }
  } catch {
    // The failing call already showed its error toast: stay in edit mode so the user can retry.
    isSegmentProcessing.value = Boolean(audience.value.is_processing)
    return
  }
  isEditing.value = false
}

const closeAddCustomerModal = () => {
  searchText.value = ''
  addCustomerModalPropsIndex.value = 0
  initTablePagination()
  closeModal('isAddCustomerModalOpen')
}

const openModal = (modalName, customerId = null) => {
  if (customerId) {
    selectedCustomerId.value = customerId
  }
  modals[modalName].value = true
}

const closeModal = (modalName, customerId = null) => {
  if (customerId) {
    selectedCustomerId.value = customerId
  }
  modals[modalName].value = false
}

const handleDeleteAudience = async () => {
  try {
    if (areCustomersLoading.value === true || customers.value?.length > 0) {
      return
    }

    await deleteAudience(audience.value)
    router.push(`/audiences`)
  } catch (err) {
    notification.create('error', {
      text: err.response.data.message,
      timeout: 3000
    })
  } finally {
    isRemoveAudienceModalOpen.value = false
  }
}

const removeCustomerFromAudience = async () => {
  try {
    if (!selectedCustomerId.value || !audience.value) {
      throw new Error('Customer ID or audience ID is undefined.')
    }

    await axios.delete(`customers/${selectedCustomerId.value}/segments/${audience.value.id}`)

    notification.create('success', {
      text: t('pages.customer.audienceDetailPage.delete.deleted'),
      timeout: 3000
    })

    isRemoveCustomerModalOpen.value = false
    await initPage()
  } catch (error) {
    notification.create('error', {
      text: t('pages.customer.audienceDetailPage.delete.errorNotificationRemove'),
      timeout: 3000
    })
    throw new Error(`Error removing customer from audience: ${error}`)
  }
}

const handleAddNewCustomer = async () => {
  if (isCreatingCustomer.value) {
    return
  }

  isCreatingCustomer.value = true

  try {
    newCustomer.value.lists = [{ id: audienceId.value }]

    const isCustomerAdded = await addCustomer(newCustomer.value, selectedAttributes.value)

    if (isCustomerAdded) {
      isCustomerLoading.value = false
      closeAddCustomerModal()
      await initPage()
    }
  } catch (error) {
    notification.create('error', {
      text: t('pages.customer.audienceDetailPage.addCustomer.errorNotificationAdd')
    })
    throw new Error(`Error adding new customer: ${error}`)
  } finally {
    isCreatingCustomer.value = false
  }
}

const handleAddCustomer = (id, data) => {
  handleAddCustomerFromSearchList(id)
  data.add = false
}
const handleAddCustomerFromSearchList = async (customerId) => {
  if (!customerId || !audienceId.value) {
    return
  }

  try {
    await associateSegmentToCustomer(customerId, [audienceId.value])

    const customer = searchList.value.find((customer) => customer.id === customerId)
    if (customer) {
      customer.add = false
    }

    await initPage()
  } catch (error) {
    notification.create('error', {
      text: 'An error occurred while adding the customer. Please try again.'
    })
    throw new Error(`Error adding customer from search list: ${error}`)
  }
}

const addCustomerModalPropsIndex = ref(0)

watch(addCustomerModalPropsIndex, (step) => {
  if (step === 2) {
    searchText.value = ''
    searchCustomer(null)
  }
})

const addCustomerModalProps = [
  {
    title: t('pages.customer.audienceDetailPage.addCustomer.choice.title')
  },
  {
    title: t('pages.customer.audienceDetailPage.addCustomer.new.title'),
    linkText: t('pages.customer.audienceDetailPage.edit.linkText'),
    cancel: t('global.words.cancel'),
    callBackFn: handleAddNewCustomer
  },
  {
    title: t('pages.customer.audienceDetailPage.addCustomer.search.title'),
    callBackFn: () => {
      addCustomerModalPropsIndex.value = 0
      closeModal('isAddCustomerModalOpen')
    }
  }
]

const initPage = async (withProcess = false) => {
  try {
    await getCustomersCount()
    await Promise.all([getAudience(), getCustomers(withProcess), getAutopilots()])
  } catch (error) {
    notification.create('error', {
      text: t('pages.customer.audienceDetailPage.notifications.loading.error')
    })
    throw new Error(`Error initializing page: ${error}`)
  } finally {
    isLoading.value = false
  }
}

const openFlow = (flowId) => {
  router.push(`/marketing-flow/detail/${flowId}`)
}

onMounted(async () => {
  await initPage(true)
  if (route.query.create) {
    isSegmentProcessing.value = true
  }
  isLoading.value = false
})

const changePage = async (page) => {
  table.pagination.currentPage = page
  table.pagination.page = page
  await getCustomers()
}

onBeforeMount(() => {
  const audienceParam = route.params.audienceId

  if (audienceParam) {
    audienceId.value = Array.isArray(audienceParam) ? parseInt(audienceParam[0], 10) : parseInt(audienceParam, 10)
  }
})
</script>
<template>
  <section v-if="!isLoading">
    <div
      v-if="audience"
      class="flex flex-col gap-4 px-6 pb-6 pt-6 sm:px-12 sm:pb-8 sm:pt-8 lg:flex-row lg:items-end lg:justify-between"
    >
      <div class="min-w-0">
        <router-link
          :to="{ name: 'AudienceList' }"
          class="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft class="size-4" />
          {{ t('pages.customer.audiences.header.crumbs') }}
        </router-link>
        <div class="mt-2 flex items-center gap-3">
          <AudienceTitle
            ref="audienceTitleRef"
            :title="pageTitle.name"
            :is-loading="isHeaderLoading"
            @update:title="handleUpdateTitle"
          />
          <Badge
            v-if="contactCountValue"
            variant="neutral"
            class="shrink-0 text-sm"
            :title="contactCountLabel ?? undefined"
          >
            {{ contactCountValue }}
          </Badge>
        </div>
        <div class="mt-2.5 flex flex-wrap items-center gap-2">
          <AudienceStoreChips :stores="audience.stores" :merchant-stores="audience.merchant_stores" />
          <ContentScopeDetailBadge
            v-if="!isPongoGeneratedAudience"
            :scope="audience.scope"
            subject="audience"
            :save="handleUpdateScope"
          />
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <DropdownMenu v-if="!isEditing">
          <DropdownMenuTrigger as-child>
            <Button variant="outline">
              {{ t(`pages.customer.audienceDetailPage.actions.${audience?.type}.dropdownLabel`) }}
              <ChevronDown class="ml-auto" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              v-for="action in actions"
              :key="action.id"
              :variant="action.variant"
              @click="handleDropdownUpdate(action.id)"
            >
              <component :is="action.icon" />
              {{ action.text }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          v-else
          type="button"
          variant="default-primary"
          :disabled="blockingMarketingFlows.length > 0 || hasRulesError"
          @click="saveRules"
        >
          {{ t('pages.customer.audienceDetailPage.dynamic.edit.save') }}
        </Button>
      </div>
    </div>

    <BaseTip v-if="isTipVisible" class="mx-6 mb-6 w-auto sm:mx-12" severity="info">
      <template #icon>
        <Clock class="self-center" />
      </template>
      <template #text>
        <p>{{ t('pages.customer.audienceDetailPage.emptyState.processing.description') }}</p>
      </template>
      <template #button>
        <Button type="button" variant="link" class="h-auto py-0 text-blue-700" @click="getCustomers(true)">
          <RefreshCw />
          {{ t('global.words.refresh') }}
        </Button>
      </template>
    </BaseTip>

    <div class="space-y-8 px-12 pb-6">
      <BaseTab v-model="currentTab" :tabs="tabs" class="w-full" @update:model-value="changeTab">
        <template #contacts>
          <TheEmptyState
            v-if="isEmptyStateVisible"
            module="customer"
            :title="emptyState[audience?.type]?.title"
            :description="emptyState[audience?.type]?.description"
            :secondary-button="emptyState[audience?.type]?.secondaryButton"
            :primary-button="emptyState[audience?.type]?.primaryButton"
            @on-click="
              (event) => {
                if (event === 'primaryButton') {
                  emptyState[audience?.type]?.primaryButton.callBackFn()
                } else {
                  emptyState[audience?.type]?.secondaryButton.callBackFn()
                }
              }
            "
          />
          <template v-else>
            <AppTable
              :columns="table.columns"
              :is-loading="areCustomersLoading || isSegmentProcessing"
              :rows="rows"
              :pagination="table.pagination"
              @update:change-page="changePage"
            >
              <template #td-customer="{ data }">
                <RowCustomer
                  :id="data.customer.id"
                  :country-flag="data.customer.countryFlag ?? null"
                  :email="data.customer.email"
                  :loyalty-card-number="data.customer.loyaltyCardNumber"
                  :has-optin="data.customer.hasOptin"
                  :name="data.customer.name"
                  :phone="data.customer.phone"
                />
              </template>
              <template #td-options="{ id }">
                <span class="relative inline-flex">
                  <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                      <button
                        class="relative inline-flex items-center rounded-md px-2 py-2 text-sm font-medium text-foreground focus:outline-hidden"
                      >
                        <BaseIcon
                          class="-ml-1 h-5 w-5 text-foreground-secondary"
                          name="HeroEllipsisHorizontalIconSolid"
                        />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" class="w-fit">
                      <DropdownMenuItem
                        class="text-red-500 hover:text-red-600 cursor-pointer"
                        @click="openModal('isRemoveCustomerModalOpen', id)"
                      >
                        {{ t('pages.customer.audienceDetailPage.table.options.deleteUser') }}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </span>
              </template>
              <template #loader>
                <TableCell>
                  <div class="flex items-center gap-3">
                    <Skeleton class="size-9 rounded-full" />
                    <div class="flex flex-col gap-1.5">
                      <Skeleton class="h-3 w-32" />
                      <Skeleton class="h-2 w-24" />
                    </div>
                  </div>
                </TableCell>
                <TableCell v-for="i in 3" :key="i">
                  <Skeleton class="h-3 w-28" />
                </TableCell>
              </template>
            </AppTable>
          </template>
        </template>
        <template #rules>
          <BaseTip v-if="isEditing && blockingMarketingFlows.length > 0" severity="warn" class="mb-6">
            <template #text>
              <p>{{ t('pages.audience.audienceDetailPage.disabledEditDisclaimer', blockingMarketingFlows.length) }}</p>
              <ul class="list-disc pl-4 text-sm">
                <li
                  v-for="flow in blockingMarketingFlows"
                  :key="flow.id"
                  class="cursor-pointer hover:underline"
                  @click="openFlow(flow.id)"
                >
                  {{ flow.name }}
                </li>
              </ul>
            </template>
          </BaseTip>

          <AudienceRulesPage
            :audience-id="audienceId"
            :is-editing="isEditing"
            :initial-store-ids="audience.store_ids ?? []"
            :merchant-stores="audience.merchant_stores ?? []"
            :scoped-stores="audience.stores ?? []"
            :hide-store-scope="Boolean(audience.frequency_type)"
            @has-rules-error="hasRulesError = $event"
            @update-rules="newRules = $event"
            @update:store-ids="editedStoreIds = $event"
            @update:audience-customers-new-count="audienceCustomersNewCount = $event"
          />
        </template>
      </BaseTab>
    </div>
  </section>
  <TheLoadingPage v-else />
  <BaseModal
    :is-open="isEditStoresModalOpen"
    :heading-title="t('pages.customer.audienceDetailPage.editStores.title')"
    :link-text="t('pages.customer.audienceDetailPage.editStores.linkText')"
    :cancel="t('global.words.cancel')"
    :is-processing="isSavingStores"
    @close="isEditStoresModalOpen = false"
    @click="handleSaveStores"
  >
    <AudienceStoreSelectField v-model="editingStoreIds" :additional-store-options="additionalStoreOptionsForEdit" />
  </BaseModal>

  <BaseModal
    :is-open="isRemoveAudienceModalOpen"
    :text-title="deleteAudienceModalProps.title"
    :text="deleteAudienceModalProps.text"
    :link-text="deleteAudienceModalProps.linkText"
    :cancel="deleteAudienceModalProps.cancel"
    :icon="deleteAudienceModalProps.icon"
    :icon-color="deleteAudienceModalProps.iconColor"
    @close="isRemoveAudienceModalOpen = false"
    @click="handleDeleteAudience"
  />

  <BaseModal
    :is-open="isRemoveCustomerModalOpen"
    :text-title="deleteModalProps.customer.title"
    :text="deleteModalProps.customer.text"
    :link-text="deleteModalProps.customer.linkText"
    :cancel="deleteModalProps.customer.cancel"
    :icon="deleteModalProps.customer.icon"
    :icon-color="deleteModalProps.customer.iconColor"
    @close="isRemoveCustomerModalOpen = false"
    @click="removeCustomerFromAudience"
  />
  <BaseModal
    v-if="isAddCustomerModalOpen"
    :is-open="isAddCustomerModalOpen"
    :heading-title="addCustomerModalProps[addCustomerModalPropsIndex].title"
    :link-text="addCustomerModalProps[addCustomerModalPropsIndex].linkText"
    :cancel="addCustomerModalProps[addCustomerModalPropsIndex].cancel"
    :is-processing="isCreatingCustomer"
    @click="addCustomerModalProps[addCustomerModalPropsIndex].callBackFn"
    @close="closeAddCustomerModal"
  >
    <div
      v-if="addCustomerModalPropsIndex === 0"
      class="flex h-full flex-col justify-around gap-y-5 sm:flex-row sm:justify-center sm:space-x-6"
    >
      <button
        type="button"
        class="group flex h-full w-full flex-col items-center justify-center rounded-lg border p-6 text-center text-foreground hover:border-primary hover:bg-primary/5"
        @click="addCustomerModalPropsIndex = 2"
      >
        <Users class="mb-3 aspect-square h-6 group-hover:text-primary" />
        <label class="mb-1 text-sm font-medium">
          {{ t('pages.customer.audienceDetailPage.addCustomer.knownCustomer.label') }}
        </label>
        <p class="text-sm font-normal text-foreground-secondary">
          {{ t('pages.customer.audienceDetailPage.addCustomer.knownCustomer.description') }}
        </p>
      </button>
      <button
        type="button"
        class="group flex h-full w-full flex-col items-center justify-center rounded-lg border p-6 text-center text-foreground hover:border-primary hover:bg-primary/5"
        @click="addCustomerModalPropsIndex = 1"
      >
        <BaseIcon class="mb-3 aspect-square h-6 group-hover:text-primary" name="HeroUserPlusIconSolid" />
        <label class="mb-1 text-sm font-medium">
          {{ t('pages.customer.audienceDetailPage.addCustomer.newCustomer.label') }}
        </label>
        <p class="text-sm font-normal text-foreground-secondary">
          {{ t('pages.customer.audienceDetailPage.addCustomer.newCustomer.description') }}
        </p>
      </button>
    </div>
    <CustomerModal
      v-else-if="addCustomerModalPropsIndex === 1"
      v-model:new-customer="newCustomer"
      v-model:errors="customerErrors"
      is-creation-mode
      :selected-attributes="selectedAttributes"
      :genders-list="gendersList"
      :attributes="merchantAttributes"
      :scoped-stores="audience?.stores ?? []"
    />
    <div v-else-if="addCustomerModalPropsIndex === 2" class="flex h-full flex-col">
      <div class="relative mb-6 mt-auto flex">
        <Input
          v-model="searchText"
          type="search"
          :placeholder="t('ui.searchPlaceholder')"
          @input="searchCustomer(null)"
        />
      </div>
      <div class="my-auto rounded-lg">
        <template v-if="!isSearchingCustomers">
          <TheEmptyState
            v-if="searchList.length < 1"
            module="customer"
            :title="t('pages.customer.audienceDetailPage.search.empty')"
          />
          <AppTable
            v-else
            :rows="searchList"
            :columns="searchTable.columns"
            :pagination="searchTable.pagination"
            @update:change-page="searchCustomer"
          >
            <template #td-customer="{ data }">
              <RowCustomer
                :id="data.customer.id"
                :country-flag="data.customer.countryFlag ?? null"
                :email="data.customer.email"
                :loyalty-card-number="data.customer.loyaltyCardNumber"
                :has-optin="data.customer.hasOptin"
                :name="data.customer.name"
                :phone="data.customer.phone"
              />
            </template>
            <template #td-add="{ id, data }">
              <div class="flex justify-end">
                <Button v-if="data.add" variant="outline" @click="handleAddCustomer(id, data)">
                  {{ t('pages.customer.audienceDetailPage.addCustomer.search.addBtn') }}
                </Button>
                <button
                  v-else
                  data-disabled="true"
                  class="rounded-md border border-green-500 px-4 py-2 hover:cursor-default"
                >
                  <span class="inline-flex items-center text-sm font-medium text-green-500"
                    >{{ t('pages.customer.audienceDetailPage.addCustomer.search.added')
                    }}<BaseIcon class="ml-2 aspect-square w-5" name="HeroCheckIconOutline" />
                  </span>
                </button>
              </div>
            </template>
          </AppTable>
        </template>
        <AppSkeleton v-else class="flex w-full" />
      </div>
    </div>
  </BaseModal>
</template>
